import type { EnrichedOrder } from "@/features/order/types";
import type { Product } from "@/features/product/types";
import type { EnrichedQuotationItem } from "./types";

export type DisplayItem =
  | (EnrichedQuotationItem & { isUnavailable?: false })
  | {
      id: string;
      product_name: string;
      product_category: string;
      isUnavailable: true;
    };

export function getEligibleOrdersForQuotation(
  orders: EnrichedOrder[],
  activeQuotationId: string,
) {
  return orders.filter(
    (order) =>
      order.quotation_id === activeQuotationId &&
      order.status === "open" &&
      order.items.length > 0,
  );
}

export function getOrderTotal(order: EnrichedOrder) {
  return order.items.reduce((sum, item) => sum + item.charged_price, 0);
}

export function findOrderItemForQuotation(
  order: EnrichedOrder,
  quotationItemId: string,
) {
  return order.items.find((item) => item.quotation_item_id === quotationItemId);
}

export function buildClientDisplay(
  items: EnrichedQuotationItem[],
  allProducts: Product[],
) {
  const quotedProductIds = new Set(items.map((item) => item.product_id));
  const offers = items.filter((item) => item.is_offer);
  const grouped = items
    .filter((item) => !item.is_offer)
    .reduce<Record<string, DisplayItem[]>>((acc, item) => {
      (acc[item.product_category] ??= []).push(item);
      return acc;
    }, {});

  for (const product of allProducts) {
    if (quotedProductIds.has(product.id)) continue;
    (grouped[product.category] ??= []).push({
      id: `unavailable-${product.id}`,
      product_name: product.name,
      product_category: product.category,
      isUnavailable: true,
    });
  }

  return { offers, grouped };
}

export function sortDisplayItems(a: DisplayItem, b: DisplayItem) {
  if (a.isUnavailable && !b.isUnavailable) return 1;
  if (!a.isUnavailable && b.isUnavailable) return -1;
  return a.product_name.localeCompare(b.product_name);
}
